/* eslint-disable no-template-curly-in-string */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ModalBody, PopoverBody, Collapse } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import {
  DetailsView,
  FileManagerComponent,
  Inject,
  NavigationPane,
  Toolbar,
} from '@syncfusion/ej2-react-filemanager';
import { Browser } from '@syncfusion/ej2-base';
import { useTranslation } from 'react-i18next';
import { ContextMenuComponent } from '@syncfusion/ej2-react-navigations';

import {
  allowedExtensions,
  ALERT_TYPE_ERROR,
  ALERT_TYPE_SUCCESS,
  validExtensionTypes,
} from 'shared/utils';
import { ModalHeader } from 'shared/components/modal/ModalHeaders';
import { colors } from 'shared/defaultTheme';
import AlertModal from 'shared/components/AlertModal';
import UploadDocuments from 'shared/components/UploadDocuments';
import ConfirmModal from 'shared/components/ConfirmModal';
import MainSpinner from 'shared/components/MainSpinner';
import { FileSizeNumber } from 'shared/helpers/fileSize/fileSize';
import { Checkbox } from 'shared/components/form/CheckBox';

import { logout, saveGridHeader, saveGridContent } from 'redux/actions/authActions';
import {
  getDocumentDetails,
  getReadDocument,
  documentFailure,
  documentSuccessUpload,
  setUploadDocuments,
  setRefreshUpload,
  refreshPath,
} from 'redux/actions/documentActions';
import { showAlert, closeAlert } from 'redux/actions/alertActions';
import useApi from 'hooks/useApi';
import { rememberEditCookies } from 'redux/actions/layoutActions';
import {
  changeDefaultLanguage,
  changeDefaultLanguageCustom,
  changeSearchValue,
} from 'redux/actions/topBarActions';

import { ReactComponent as CaretDownIcon } from 'assets/icon-angle-down.svg';
import { ReactComponent as CaretUpIcon } from 'assets/icon-angle-up.svg';

import PublicDocuments from './PublicDocuments';
import {
  FormModal as PublicFormModal,
  FormModal as UploadFormModal,
  PopoverFilter,
  OptionMenuFilter,
} from '../styles';

const MAX_FILE_SIZE = 124288000;
const VALID_EXTENSION = '1';
const INVALID_EXTENSION = '0';

const FileManager = ({
  clientId,
  onToolbarCreate,
  onToolbarClick,
  onMenuClick,
  onMenuOpen,
  onActionAddClick,
  onPopupOpen,
  onPopUpClose,
  refMenu,
  refFileManager,
  setLoadingCreateFolder,
  loadingCreateFolder,
  setTotalSizeUpload,
  totalSizeUpload,
  isInputFile,
  setInputFile,
}) => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const { customAreaApi } = useApi();
  const {
    cusNavId,
    isCustomer,
    mail,
    name,
    surname,
    authToken,
    i18,
    getGridHeader,
    getGridContent,
  } = useSelector((state) => state.auth);
  const currentClient = useSelector((state) => state.clientState?.currentClient);
  const { openAlert, alertType, alertMessage } = useSelector((state) => state.alertState);
  const { selectedDefaultLanguage } = useSelector((state) => state.topBar);
  const { readDocument, uploadDocuments, emailToContactsOnPublish } = useSelector(
    (state) => state.document
  );
  const { selectedDefaultLanguageCustom } = useSelector((state) => state.topBar);
  const [isFailRead, setIsFailRead] = useState(false);
  const [openPublicDocumentsModal, setOpenPublicDocumentsModal] = useState(false);
  const [openUploadDocumentsModal, setOpenUploadDocumentsModal] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [showWarning, setShowWarning] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showConfirmExistFile, setShowConfirmExistFile] = useState(false);
  const [newUploadFiles, setNewUploadFiles] = useState([]);
  const [isSendNotify, setIsSendNotify] = useState(false);
  const [filterList, setFilterList] = useState([
    { id: 0, isOpen: false, name: t('general_states') },
    { id: 1, isOpen: false, name: t('Settings_labels') },
  ]);
  const [selectedState, setSelectedState] = useState([]);
  const [confirmMessages, setConfirmMessages] = useState([]);
  const [selectedTag, setSelectedTag] = useState([]);
  const [states, setStates] = useState([]);
  const [tags, setTags] = useState([]);
  const [defaultFolders, setDefaultFolders] = useState([]);
  const newData = useRef([]);
  const newDirectoryData = useRef([]);

  const [{ loading: loadingUpdate }, updatePublicDocument] = customAreaApi(
    {
      method: 'POST',
    },
    { manual: true }
  );

  const [, getCheckItemExists] = customAreaApi(
    {
      method: 'POST',
    },
    { manual: true }
  );

  const [, getData] = customAreaApi(
    {
      method: 'GET',
    },
    { manual: true }
  );

  useEffect(() => {
    const getList = async () => {
      const {
        data: { data },
      } = await getData({
        url: `${window.API_V1}/cus/states/${cusNavId}`,
      });
      setStates(data);
      const {
        data: { data: tagData },
      } = await getData({
        url: `${window.API_V1}/cus/tags/${cusNavId}`,
      });
      setTags(tagData);
    };
    getList();
    return () => {
      dispatch(getDocumentDetails(null));
      // dispatch(getReadDocument(null));
      dispatch(refreshPath(true));
    };
  }, []);

  const onCloseAlert = () => {
    dispatch(closeAlert());
    setTotalSizeUpload(0);
  };

  const onToggleWarning = () => {
    setShowWarning((currentShow) => !currentShow);
  };

  useEffect(() => {
    const footer = document.querySelector('.e-footer-content');
    if (footer && loadingCreateFolder) {
      const btnCreateFolder = footer.querySelector('.e-btn');
      btnCreateFolder.disabled = true;
      btnCreateFolder.style.backgroundColor = colors.suvaGrey;
    }
  }, [loadingCreateFolder]);

  const handleCheckItemExists = async (useNewData = false) => {
    const nameFile = (useNewData ? newData.current : newUploadFiles).map((x) => {
      return x.pathNameFile ? x.pathNameFile : x.name;
    });
    const {
      data: { data },
    } = await getCheckItemExists({
      url: `${window.API_V1}/cli/items/checkitemexists/${cusNavId}/${clientId}/${encodeURIComponent(
        mail
      )}?parentPathId=${refFileManager.current.path}`,
      data: nameFile,
    });
    return data;
  };

  const getFiles = (file) => {
    const existExtensions = validExtensionTypes.some(
      (x) => file.name.toUpperCase().endsWith(x) || file.name.toLowerCase().endsWith(x)
    );
    const type = file.name.split('.');
    const newDataFile = {
      id: file.name,
      name: file.name,
      rawFile: file,
      size: file.size,
      statusCode: existExtensions ? VALID_EXTENSION : INVALID_EXTENSION,
      type: type[type.length - 1],
      isDirectory: false,
    };
    newData.current.push(newDataFile);
  };

  const getDirectoryFiles = (item, file) => {
    const pathIds = item.fullPath.split('/');
    const clearedPathIds = pathIds.filter((x) => x.length > 0);
    const replacePathIds = clearedPathIds[clearedPathIds.length - 1];
    const fullPathDocument = item.fullPath.replace(`/${replacePathIds}`, '');
    let location = '';
    refFileManager.current.pathNames.forEach((pathName) => {
      location += `/${pathName}`;
    });
    const type = file.name.split('.');
    const existExtensions = validExtensionTypes.some(
      (x) => `.${type[type.length - 1].toLowerCase()}` === x
    );
    const newDataFile = {
      id: file.name,
      pathName: `${location}${fullPathDocument}`,
      pathNameFile: `${item.fullPath.substring(1, item.fullPath.length)}`,
      name: file.name,
      rawFile: file,
      size: file.size,
      statusCode: existExtensions ? VALID_EXTENSION : INVALID_EXTENSION,
      type: type[type.length - 1],
      isDirectory: true,
    };
    newData.current.push(newDataFile);
  };

  const getDirectory = (item) => {
    return new Promise((resolve) => {
      if (item.isFile) {
        item.file((file) => {
          getDirectoryFiles(item, file);
          resolve();
        });
      } else if (item.isDirectory) {
        const dirReader = item.createReader();
        dirReader.readEntries(async (entries) => {
          const promises = [];
          newDirectoryData.current.push({ item, entries: [...entries] });
          for (let i = 0; i < entries.length; i += 1) {
            promises.push(getDirectory(entries[i]));
          }
          if (promises.length > 0) {
            await Promise.all(promises);
          }
          resolve();
        });
      }
    });
  };

  const uploadEntries = async () => {
    const currentTotalSizeUpload = newData.current.reduce((previousValue, currentValue) => {
      return previousValue + currentValue.size;
    }, 0);
    const uploadSize = FileSizeNumber(currentTotalSizeUpload);
    if (parseInt(uploadSize, 10) > 124) {
      dispatch(
        showAlert({
          alertType: ALERT_TYPE_ERROR,
          alertMessage: t('new_document_text_maximum_size_upload'),
        })
      );
    } else {
      if (newDirectoryData.current.length > 0) {
        const foundFiles = newDirectoryData.current.some((x) => x.entries.length !== 0);
        if (!foundFiles) {
          dispatch(
            showAlert({
              alertType: ALERT_TYPE_ERROR,
              alertMessage: t('document_not_found_directory'),
            })
          );
          return;
        }
      }
      const existItems = await handleCheckItemExists(true);
      if (existItems.rename) {
        let newNameFile = [];
        let location = '';
        refFileManager.current.pathNames.forEach((pathName) => {
          location += `/${pathName}`;
        });
        newData.current.forEach((newUploadFile) => {
          let existsName = '';
          if (newUploadFile.pathNameFile !== undefined) {
            existsName = existItems.namesFinal[newUploadFile.pathNameFile];
          } else {
            existsName = existItems.namesFinal[newUploadFile.name];
          }
          if (existsName) {
            newNameFile = [
              ...newNameFile,
              t('documents_exits_file_messages', {
                interpolation: { escapeValue: false },
                oldName: newUploadFile.name,
                newName: existsName,
                route: location,
              }),
            ];
          }
        });
        setConfirmMessages(newNameFile);
        setShowConfirmExistFile(true);
      } else {
        const uploadFileDocuments = newData.current.map((x) => {
          return { ...x, errorUpload: null };
        });
        setNewUploadFiles(uploadFileDocuments);
        setOpenUploadDocumentsModal(true);
      }
    }
  };

  const handleDropArea = useCallback(async (event) => {
    event.preventDefault();
    newData.current = [];
    newDirectoryData.current = [];
    const { items } = event.dataTransfer;
    const promises = [];
    for (let index = 0; index < items.length; index += 1) {
      const item = items[index].webkitGetAsEntry();
      if (item.isFile) {
        promises.push(
          new Promise((resolve) => {
            item.file((file) => {
              getFiles(file, items.length, index);
              resolve();
            });
          })
        );
      } else if (item.isDirectory) {
        promises.push(getDirectory(item));
      }
    }
    if (promises.length > 0) {
      await Promise.all(promises);
    }
    await uploadEntries();
    dispatch(setRefreshUpload(true));
  }, []);

  useEffect(async () => {
    document
      .getElementById('file')
      ?.ej2_instances[0].uploadObj.dropArea.addEventListener('drop', handleDropArea);
    return () => {
      document
        .getElementById('file')
        ?.ej2_instances[0].uploadObj.dropArea.removeEventListener('drop', handleDropArea);
    };
  });

  useEffect(() => {
    const rememberEdit = document.cookie.indexOf('rememberEdit');
    if (rememberEdit !== -1) {
      dispatch(rememberEditCookies(document.cookie));
    } else {
      dispatch(rememberEditCookies(null));
    }
  }, []);

  const detailsViewSettingsModel = {
    columns: [
      {
        field: 'type',
        headerText: t('panel_table_type'),
        width: '26px',
        template: isCustomer
          ? '<div class="type-container has-permission-write-${permission?.write} d-flex align-items-end"><span class="file-${type} pathId-${pathId} folderDefault-${isFolderDefault}"></span><span class=alert-${alerts.length}></span></div>'
          : '<div class="type-container has-permission-write-${permission?.write} d-flex align-items-end"><span class="file-${type}"></span><span class=alert-${alerts.length}></span></div>',
      },
      {
        field: 'name',
        headerText: t('panel_table_name'),
        width: '200px',
      },
      {
        field: 'dateCreated',
        headerText: t('panel_table_height'),
        type: 'dateTime',
        format: 'dd/MM/yyyy HH:mm',
        width: '140px',
      },
      {
        field: '_fm_modified',
        headerText: t('panel_table_date'),
        type: 'dateTime',
        format: 'dd/MM/yyyy HH:mm',
        width: '140px',
      },
      {
        field: 'createdBy',
        headerText: t('panel_table_owner'),
        width: '140px',
        template:
          '${if(createdBy)}<div class="d-flex" data-toggle="tooltip" data-placement="bottom" title="${createdBy?.nameComplete}"><p class="nameComplete">${createdBy?.nameComplete}</p></div>${/if}',
      },
      {
        field: 'public',
        headerText: t('panel_table_public'),
        template:
          '<div>${if(readers.length > 0)}<div class="public-box-true"><span class="is-true"></span></div>${else}<div class="public-box-false"><span class="is-false"></span></div>${/if}</div>',
        width: '110px',
      },
      {
        field: 'size',
        headerText: 'Size',
        template: '<div><span>${sizeMegabytes} MB</span></div>',
        width: '110px',
      },

      {
        field: 'state',
        headerText: t('general_states'),
        width: '110px',
        template:
          '${if(stateData)}<div class="state-color-box state-color-${stateData.color}" style=background-color:${stateData.color}>${for(i18 of stateData.i18List)} ${if(i18.i18Code===clientI18)}<div class="state-color-box" data-toggle="tooltip" data-placement="bottom" title="${i18.i18Name}"><span>${i18.i18Name}</span></div>${/if} ${/for}</div>${/if}',
      },
    ],
    columnResizing: true,
  };

  useEffect(() => {
    if (selectedDefaultLanguage && readDocument) {
      const lengthFolder = refFileManager.current?.pathId.length;
      const lastFolderSelected = refFileManager.current?.pathId[lengthFolder - 1];
      const filterFolders = refFileManager.current?.feFiles[lastFolderSelected];
      filterFolders?.forEach((filterFile) => {
        if (filterFile.state) {
          const selectedI18 = filterFile;
          selectedI18.clientI18 = selectedDefaultLanguage;
        }
      });
    }
  }, [selectedDefaultLanguage, readDocument]);

  useEffect(() => {
    if (selectedTag.length > 0 || selectedState.length > 0) {
      refFileManager.current.refresh();
    }
  }, [selectedTag, selectedState]);

  const getResizingColumns = () => {
    const parser = new DOMParser();
    const getGridHeaderParser = parser.parseFromString(getGridHeader, 'text/html');
    const gridHeaderParser = getGridHeaderParser.querySelector('body > table');
    const getGridContentParser = parser.parseFromString(getGridContent, 'text/html');
    const gridContentParser = getGridContentParser.querySelector('body > table');
    const tableHeader = document.querySelector('.e-sortfilter');
    if (tableHeader) {
      tableHeader.style.width = gridHeaderParser.style.width;
      const oldGridHeaderGroup = gridHeaderParser.querySelector('#file_gridcolgroup');
      const newGridHeaderGroup = tableHeader.querySelector('#file_gridcolgroup');

      const oldHeaderColList = oldGridHeaderGroup.getElementsByTagName('col');
      const newHeaderColList = newGridHeaderGroup.getElementsByTagName('col');

      if (oldHeaderColList.length === newHeaderColList.length) {
        for (let i = 0; i < 8; i += 1) {
          const oldHeaderCol = oldHeaderColList[i];
          const newContentCol = newHeaderColList[i];
          newContentCol.style.width = oldHeaderCol.style.width;
        }
      }
    }

    const tableContent = document.querySelector('#file_grid_content_table');
    if (tableContent) {
      const oldGridContentGroup = gridContentParser.querySelector('#content-file_gridcolgroup');
      const newGridContentGroup = tableContent.querySelector('#content-file_gridcolgroup');

      const oldContentColList = oldGridContentGroup.getElementsByTagName('col');
      const newContentColList = newGridContentGroup.getElementsByTagName('col');
      if (oldContentColList.length === newContentColList.length) {
        for (let i = 0; i < 8; i += 1) {
          const oldContentCol = oldContentColList[i];
          const newContentCol = newContentColList[i];
          newContentCol.style.width = oldContentCol.style.width;
        }
      }
    }
  };

  const toolbarSettings = {
    items: Browser.isDevice
      ? [
          'Selection',
          'SortBy',
          'View',
          'Refresh',
          'NewWord',
          'NewFolder',
          'Upload',
          'Cut',
          'Copy',
          'Delete',
          'Download',
          'Rename',
        ]
      : ['Selection', 'FiltersCli', 'SortBy', 'View', 'Refresh', 'Actions'],
    visible: true,
  };
  const contextMenuSettingsSingle = {
    file: [
      'property',
      'Open',
      'Cut',
      'Copy',
      'Paste',
      'Rename',
      'Download',
      'create_communication',
      'Delete',
      'document_share_permission',
      'un_public_document',
    ],
    folder: [
      'NewFolder',
      'property',
      'Open',
      'Cut',
      'Copy',
      'Paste',
      'Rename',
      'Delete',
      'document_share_permission',
      'un_public_document',
      'Download',
    ],
    layout: ['Paste', 'NewFolder', 'new_word', 'upload'],
    visible: true,
  };

  const actionAddContextMenuItems = [
    {
      id: 'new_folder',
      text: t('documents_new_folder'),
      iconCss: 'lf-icon-folder',
    },
    {
      id: 'new_word',
      text: t('documents_new_word'),
      iconCss: 'lf-icon-document',
    },
    {
      id: 'upload',
      text: t('documents_upload_document'),
      iconCss: 'lf-icon-up-file',
    },
  ];

  const getDocumentions = (event, ajaxSetting) => {
    const data = ajaxSetting;
    const ajaxUrl = event;
    const { ajaxSettings } = event;
    const grid = document.querySelector('.e-sortfilter');
    const gridContent = document.querySelector('#file_grid_content_table');
    if (grid && gridContent && !Browser.isDevice) {
      dispatch(saveGridHeader(grid.outerHTML));
      dispatch(saveGridContent(gridContent.outerHTML));
    }
    const xhr = new XMLHttpRequest();
    xhr.open(
      'POST',
      `${event.ajaxSettings.url}${cusNavId}/${clientId}/${encodeURIComponent(mail)}/${
        ajaxSetting.action
      }`,
      true
    );
    xhr.onload = () => {
      if (xhr.status === 401) {
        dispatch(logout());
        i18n.changeLanguage(isCustomer ? selectedDefaultLanguageCustom : i18);
        dispatch(changeDefaultLanguage(null));
        dispatch(changeDefaultLanguageCustom(null));
        dispatch(changeSearchValue(''));
      } else {
        setTimeout(() => {
          const responseData = JSON.parse(xhr.responseText);
          const isFolderDefault =
            responseData.files?.filter((el) => el.isFolderDefault === true) || [];
          const isFolderDefaultNames = isFolderDefault.map((el) => el.name);
          const i18Buzon = ['Buzón', 'Bústia', 'Sarrera', 'Mailbox'];
          const filteredArray = isFolderDefaultNames.filter(
            (element) => !i18Buzon.includes(element)
          );
          setDefaultFolders(filteredArray);
        }, 100);
      }
    };
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.setRequestHeader('Accept', 'text/plain');
    xhr.setRequestHeader('Authorization', `Bearer ${authToken}`);
    xhr.send(JSON.stringify(ajaxSetting));

    if (selectedState.length > 0) {
      data.states = [...selectedState];
      ajaxSettings.data = JSON.stringify(data);
    }

    if (selectedTag.length > 0) {
      data.tags = [...selectedTag];
      ajaxSettings.data = JSON.stringify(data);
    }

    ajaxUrl.ajaxSettings.url = `${
      event.ajaxSettings.url
    }${cusNavId}/${clientId}/${encodeURIComponent(mail)}/${ajaxSetting.action}`;
  };

  const createFolderDocument = (event, ajaxSetting) => {
    setLoadingCreateFolder(true);
    const { data } = ajaxSetting;
    const { ajaxSettings } = event;
    data[0].createdBy = { nameComplete: `${name} ${surname}`, mail };
    data[0].description = '';
    ajaxSettings.data = JSON.stringify(ajaxSetting);
    const ajaxUrl = event;
    ajaxUrl.ajaxSettings.url = `${
      event.ajaxSettings.url
    }${cusNavId}/${clientId}/${encodeURIComponent(mail)}/${ajaxSetting.action}`;
  };

  const getNewRename = (event, ajaxSetting) => {
    const ajaxUrl = event;
    ajaxUrl.ajaxSettings.url = `${
      event.ajaxSettings.url
    }${cusNavId}/${clientId}/${encodeURIComponent(mail)}/${ajaxSetting.action}`;
  };

  const removeDocument = (event, ajaxSetting) => {
    if (isSendNotify !== undefined) {
      const ajaxUrl = event;
      ajaxUrl.ajaxSettings.url = `${
        event.ajaxSettings.url
      }${cusNavId}/${clientId}/${encodeURIComponent(mail)}/${
        ajaxSetting.action
      }?sendNotify=${isSendNotify}`;
    } else {
      const ajaxUrl = event;
      ajaxUrl.ajaxSettings.url = `${
        event.ajaxSettings.url
      }${cusNavId}/${clientId}/${encodeURIComponent(mail)}/${ajaxSetting.action}`;
    }
  };

  const copyDocument = (event, ajaxSetting) => {
    const ajaxUrl = event;
    ajaxUrl.ajaxSettings.url = `${
      event.ajaxSettings.url
    }${cusNavId}/${clientId}/${encodeURIComponent(mail)}/${ajaxSetting.action}`;
  };

  const moveDocument = (event, ajaxSetting) => {
    const ajaxUrl = event;
    ajaxUrl.ajaxSettings.url = `${
      event.ajaxSettings.url
    }${cusNavId}/${clientId}/${encodeURIComponent(mail)}/${ajaxSetting.action}`;
  };

  const searchDocument = (event, ajaxSetting) => {
    const grid = document.querySelector('.e-sortfilter');
    const gridContent = document.querySelector('#file_grid_content_table');
    if (grid && gridContent) {
      dispatch(saveGridHeader(grid.outerHTML));
      dispatch(saveGridContent(gridContent.outerHTML));
    }
    const ajaxUrl = event;
    ajaxUrl.ajaxSettings.url = `${
      event.ajaxSettings.url
    }${cusNavId}/${clientId}/${encodeURIComponent(mail)}/${ajaxSetting.action}`;
  };

  const getUploadDocument = async (event) => {
    const actionCancel = event;
    actionCancel.cancel = true;
    if (isInputFile) {
      setInputFile(false);
      const uploadSize = parseInt(totalSizeUpload, 10);
      if (event.action === 'Upload' && uploadSize > 124) {
        dispatch(
          showAlert({
            alertType: ALERT_TYPE_ERROR,
            alertMessage: t('new_document_text_maximum_size_upload'),
          })
        );
      } else {
        setTotalSizeUpload(0);
        const existItems = await handleCheckItemExists(false);
        if (existItems.rename) {
          let newNameFile = [];
          let location = '';
          refFileManager.current.pathNames.forEach((pathName) => {
            location += `/${pathName}`;
          });
          newUploadFiles.forEach((newUploadFile) => {
            let existsName = '';
            if (newUploadFile.pathNameFile !== undefined) {
              existsName = existItems.namesFinal[newUploadFile.pathNameFile];
            } else {
              existsName = existItems.namesFinal[newUploadFile.name];
            }
            if (existsName) {
              newNameFile = [
                ...newNameFile,
                t('documents_exits_file_messages', {
                  interpolation: { escapeValue: false },
                  oldName: newUploadFile.name,
                  newName: existsName,
                  route: location,
                }),
              ];
            }
          });
          setConfirmMessages(newNameFile);
          setShowConfirmExistFile(true);
        } else {
          const uploadFileDocuments = newUploadFiles.map((x) => {
            return { ...x, errorUpload: null };
          });
          setNewUploadFiles(uploadFileDocuments);
          setOpenUploadDocumentsModal(true);
        }
      }
    }
  };

  const getDetails = (event, ajaxSetting) => {
    dispatch(getDocumentDetails(ajaxSetting.data[0]));
    const ajaxUrl = event;
    ajaxUrl.ajaxSettings.url = `${
      event.ajaxSettings.url
    }${cusNavId}/${clientId}/${encodeURIComponent(mail)}/${ajaxSetting.action}`;
  };

  const onBeforeSend = (event) => {
    const ajaxSettingsHeader = event.ajaxSettings;
    ajaxSettingsHeader.beforeSend = (args) => {
      args.httpRequest.setRequestHeader('Authorization', `Bearer ${authToken}`);
    };

    const ajaxSetting = JSON.parse(event.ajaxSettings.data);
    if (ajaxSetting.data?.length === 0) {
      ajaxSetting.path = '/';
      // eslint-disable-next-line no-param-reassign
      event.ajaxSettings.data = JSON.stringify(ajaxSetting);
    }

    const action = ajaxSetting.action ? ajaxSetting.action : event.action;
    switch (action) {
      case 'read':
        getDocumentions(event, ajaxSetting);
        break;
      case 'create':
        createFolderDocument(event, ajaxSetting);
        break;
      case 'delete':
        removeDocument(event, ajaxSetting);
        break;
      case 'rename':
        getNewRename(event, ajaxSetting);
        break;
      case 'copy':
        copyDocument(event, ajaxSetting);
        break;
      case 'move':
        moveDocument(event, ajaxSetting);
        break;
      case 'search':
        searchDocument(event, ajaxSetting);
        break;
      case 'Upload':
        getUploadDocument(event, ajaxSetting);
        break;
      case 'details':
        getDetails(event, ajaxSetting);
        break;
      default:
        getDocumentions(event, ajaxSetting);
        break;
    }
  };

  const onSuccess = (event) => {
    if (event.action === 'read') {
      dispatch(getReadDocument(event.result.cwd));
      setIsFailRead(false);
      refFileManager.current.refreshLayout();
      const newRefFileManager = refFileManager;
      if (!event.result.cwd.permission.write && Browser.isDevice) {
        newRefFileManager.current.toolbarSettings.items = [
          'Selection',
          'SortBy',
          'View',
          'Refresh',
          'Download',
        ];
      } else if (event.result.cwd.permission.write && Browser.isDevice) {
        newRefFileManager.current.toolbarSettings.items = [
          'Selection',
          'SortBy',
          'View',
          'Refresh',
          'NewWord',
          'NewFolder',
          'Upload',
          'Cut',
          'Copy',
          'Delete',
          'Download',
          'Rename',
        ];
      }
    }
    if (event.action === 'Upload') {
      dispatch(documentSuccessUpload(event));
    }
    setLoadingCreateFolder(false);
    if (getGridHeader && getGridContent && !Browser.isDevice) {
      getResizingColumns();
    }
  };

  const onFailure = (event) => {
    if (event.action === 'read') {
      setIsFailRead(true);
    }
    setLoadingCreateFolder(false);
    dispatch(documentFailure(event));
  };

  const beforePopupOpen = (event) => {
    if (event.popupName === 'Retry Upload') {
      const actionCancel = event;
      actionCancel.cancel = true;
    }
    if (event.popupName === 'Upload') {
      const actionCancel = event;
      actionCancel.cancel = true;
    }
  };

  const beforePopupClose = (event) => {
    if (event.popupName === 'Delete') {
      setIsSendNotify(document.querySelector('.checkbox-notify-client')?.checked);
    }
    if (event.popupName === 'Upload') {
      const actionCancel = event;
      actionCancel.cancel = true;
    }
  };

  const onPublicDocument = async () => {
    const newUploadDocument = uploadDocuments.map((document) => {
      if (document.permissions.length === 0 && document.public) {
        return { ...document, isRequired: true };
      }
      return { ...document, isRequired: false };
    });
    dispatch(setUploadDocuments([...newUploadDocument]));
    try {
      const findPublicDocument = uploadDocuments.filter((x) => x.public);
      await updatePublicDocument({
        data: isCustomer ? findPublicDocument : uploadDocuments,
        url: isCustomer
          ? `${window.API_V1}/cli/items/${cusNavId}/${
              isCustomer ? currentClient.id : clientId
            }/${encodeURIComponent(mail)}/publish?sendNotify=${emailToContactsOnPublish.toString()}`
          : `${window.API_V1}/cli/items/${cusNavId}/${clientId}/${encodeURIComponent(
              mail
            )}/publishAdmin`,
      });
      dispatch(
        showAlert({
          alertType: ALERT_TYPE_SUCCESS,
          alertMessage: t('general_success_message'),
        })
      );
      dispatch(setUploadDocuments([]));
    } catch (error) {
      dispatch(
        showAlert({
          alertType: ALERT_TYPE_ERROR,
          alertMessage: t('custom_field_operation_error_message'),
        })
      );
      dispatch(setUploadDocuments([]));
    }
  };

  const onClosePublicModal = () => {
    onPublicDocument();
    setOpenPublicDocumentsModal(false);
    dispatch(setUploadDocuments([]));
    setNewUploadFiles([]);
  };

  const onCloseUploadModal = () => {
    const UploadFilesFilter = newUploadFiles.filter(
      (x) => x.errorUpload !== null && x.errorUpload === false
    );
    setOpenUploadDocumentsModal(false);
    if (isCustomer && UploadFilesFilter.length > 0) {
      dispatch(setUploadDocuments([...UploadFilesFilter]));
      setOpenPublicDocumentsModal(true);
    }
  };

  const onClosePublicDocumentsModal = () => {
    setOpenPublicDocumentsModal(false);
    setNewUploadFiles([]);
  };

  const customDownload = (event) => {
    const xhr = new XMLHttpRequest();
    xhr.open(
      'POST',
      `${window.REACT_APP_SERVER_URL}${
        window.API_V1
      }/cli/sync/${cusNavId}/${clientId}/${encodeURIComponent(mail)}/download`,
      true
    );
    xhr.responseType = 'blob';
    xhr.onload = () => {
      if (xhr.status === 200) {
        const blob = new Blob([xhr.response], {
          type: xhr.getResponseHeader('Content-Type'),
        });
        const anchorUrl = window.URL.createObjectURL(blob);

        const anchor = document.createElement('a');
        anchor.href = anchorUrl;
        if (event.data.names.length === 1 && event.data.data[0].isFile) {
          anchor.download = event.data.data[0].name;
        } else {
          anchor.setAttribute('download', `${t('genera_files')}.zip`);
        }
        anchor.click();

        setTimeout(() => {
          URL.revokeObjectURL(anchorUrl);
        }, 100);
      }
      if (xhr.status === 400) {
        onToggleWarning();
        dispatch(
          showAlert({
            alertType: ALERT_TYPE_ERROR,
            alertMessage: t('custom_field_operation_error_message'),
          })
        );
      }
      if (xhr.status === 423) {
        onToggleWarning();
        dispatch(
          showAlert({
            alertType: ALERT_TYPE_ERROR,
            alertMessage: t('new_document_text_maximum_size_download'),
          })
        );
      }
    };
    const data = new FormData();
    data.append('downloadInput', JSON.stringify(event.data));
    // Custom Header Added to XHR
    xhr.setRequestHeader('Authorization', `Bearer ${authToken}`);
    xhr.send(data);
  };

  const beforeDownload = (event) => {
    if (event.name === 'beforeDownload') {
      const actionCancel = event;
      actionCancel.cancel = true;
      let total = 0;
      event.data.data.forEach((document) => {
        total += parseFloat(document.sizeMegabytes);
      });
      const megabytesTotal = parseInt(total, 10);
      if (megabytesTotal > 200) {
        dispatch(
          showAlert({
            alertType: ALERT_TYPE_ERROR,
            alertMessage: t('new_document_text_maximum_size_download'),
          })
        );
      } else {
        customDownload(event);
        setShowWarning(true);
      }
    }
  };

  const onBeforeImageLoad = (event) => {
    const eventImg = event;
    eventImg.imageUrl = `${window.REACT_APP_SERVER_URL}${
      window.API_V1
    }/cli/sync/${cusNavId}/${clientId}/${encodeURIComponent(mail)}/getImage?id=${
      event.fileDetails[0].id
    }`;
  };

  const onCancelExistFile = () => {
    setShowConfirmExistFile(false);
  };

  const onOkPublic = async () => {
    try {
      const findPublicDocument = uploadDocuments.filter((x) => x.public);
      await updatePublicDocument({
        data: findPublicDocument,
        url: `${window.API_V1}/cli/items/${cusNavId}/${clientId}/${encodeURIComponent(
          mail
        )}/publish`,
      });
      setShowConfirm(false);
      dispatch(
        showAlert({
          alertType: ALERT_TYPE_SUCCESS,
          alertMessage: t('general_success_message'),
        })
      );
      dispatch(setUploadDocuments([]));
      document.cookie = 'rememberEdit=true';
      dispatch(rememberEditCookies(document.cookie));
    } catch (error) {
      setShowConfirm(false);
      document.cookie = 'rememberEdit=true';
      dispatch(rememberEditCookies(document.cookie));
      dispatch(
        showAlert({
          alertType: ALERT_TYPE_ERROR,
          alertMessage: t('custom_field_operation_error_message'),
        })
      );
      dispatch(setUploadDocuments([]));
    }
  };

  const onOkExistFile = () => {
    const uploadFileDocuments = (newUploadFiles.length > 0 ? newUploadFiles : newData.current).map(
      (x) => {
        return { ...x, errorUpload: null };
      }
    );
    setShowConfirmExistFile(false);
    setNewUploadFiles(uploadFileDocuments);
    setOpenUploadDocumentsModal(true);
  };

  const onCancelPublic = () => {
    setShowConfirm(false);
  };

  const onBeforeOpen = () => {
    if (!isCustomer) {
      refMenu.current.enableItems([t('documents_new_folder')], false);
    }
  };

  const onFileDragStart = (event) => {
    if (!isCustomer && !event.fileDetails[0].isFile) {
      const actionCancel = event;
      actionCancel.cancel = true;
    }
  };

  const getCaretIcon = (item) => {
    return item.isOpen ? (
      <CaretUpIcon className="caret" width={22} />
    ) : (
      <CaretDownIcon className="caret" width={22} />
    );
  };

  const getCurrentFilters = (filterId) => {
    setFilterList(
      filterList.map((filter) =>
        filter.id === filterId ? { ...filter, isOpen: !filter.isOpen } : filter
      )
    );
  };

  const getNameI18 = (i18List) => {
    if (i18List) {
      const findName = i18List.find((x) => x.i18Code === selectedDefaultLanguage);
      if (findName && findName?.i18Name !== '') {
        return findName?.i18Name;
      }
      return i18List[0].i18Name;
    }
    return null;
  };

  const removeState = (index) => {
    const newStates = selectedState.filter((item) => item !== index);
    if (newStates.length === 0) {
      setTimeout(() => {
        refFileManager.current.refresh();
      }, 500);
    }
    setSelectedState(newStates);
  };

  const onSelectedState = useCallback((e, state) => {
    const findState = selectedState.find((x) => x === state.id);
    if (!findState) {
      const newStates = [...selectedState, state.id];
      setSelectedState(newStates);
    } else {
      removeState(findState);
    }
  });

  const removeTag = (index) => {
    const newTags = selectedTag.filter((item) => item !== index);
    if (newTags.length === 0) {
      setTimeout(() => {
        refFileManager.current.refresh();
      }, 500);
    }
    setSelectedTag(newTags);
  };

  const onSelectedTag = useCallback((e, tag) => {
    const findTag = selectedTag.find((x) => x === tag.id);
    if (!findTag) {
      const newTags = [...selectedTag, tag.id];
      setSelectedTag(newTags);
    } else {
      removeTag(findTag);
    }
  });

  const uploadListCreate = (event) => {
    if (event.name === 'uploadListCreate' && isInputFile) {
      const megaBytes = FileSizeNumber(event.fileInfo.size);
      setTotalSizeUpload(parseInt(totalSizeUpload, 10) + parseInt(megaBytes, 10));
      setNewUploadFiles([...newUploadFiles, event.fileInfo]);
    }
  };

  useEffect(() => {
    if (isCustomer) {
      const elements = document.querySelectorAll('.e-text-content');
      elements.forEach((element) => {
        const spanElement = element.querySelector('span');
        if (spanElement) {
          const text = spanElement.innerText;
          if (text === 'Buzón') {
            const folderIcon = element.querySelector('.e-list-icon.e-fe-folder');
            if (folderIcon) {
              folderIcon.classList.add('buzon-folder');
            }
          } else if (defaultFolders.includes(text)) {
            const folderIcon = element.querySelector('.e-list-icon.e-fe-folder');
            if (folderIcon) {
              folderIcon.classList.add('default-folder');
            }
          }
        }
      });
    }
  }, [defaultFolders]);

  return (
    <div>
      {clientId && mail && (
        <>
          <FileManagerComponent
            id="file"
            ref={refFileManager}
            ajaxSettings={{
              downloadUrl: `${window.REACT_APP_SERVER_URL}${
                window.API_V1
              }/cli/sync/${cusNavId}/${clientId}/${encodeURIComponent(mail)}/download`,
              getImageUrl: `${window.REACT_APP_SERVER_URL}${
                window.API_V1
              }/cli/sync/${cusNavId}/${clientId}/${encodeURIComponent(mail)}/getImage`,
              uploadUrl: `${window.REACT_APP_SERVER_URL}${
                window.API_V1
              }/cli/sync/${cusNavId}/${clientId}/${encodeURIComponent(mail)}/upload`,
              url: `${window.REACT_APP_SERVER_URL}${window.API_V1}/cli/sync/`,
            }}
            success={onSuccess}
            failure={onFailure}
            toolbarCreate={onToolbarCreate}
            toolbarClick={onToolbarClick}
            menuClick={onMenuClick}
            menuOpen={onMenuOpen}
            popupOpen={onPopupOpen}
            popupClose={onPopUpClose}
            contextMenuSettings={contextMenuSettingsSingle}
            toolbarSettings={toolbarSettings}
            view="Details"
            locale="es"
            showHiddenItems={isCustomer}
            navigationPaneSettings={{ visible: !Browser.isDevice, sortOrder: 'Ascending' }}
            beforeSend={onBeforeSend}
            detailsViewSettings={detailsViewSettingsModel}
            height={isFailRead ? '732px' : 'auto'}
            uploadSettings={{
              maxFileSize: MAX_FILE_SIZE,
              allowedExtensions,
            }}
            beforePopupOpen={beforePopupOpen}
            beforePopupClose={beforePopupClose}
            beforeDownload={beforeDownload}
            beforeImageLoad={onBeforeImageLoad}
            fileDragStart={onFileDragStart}
            allowDragAndDrop
            uploadListCreate={uploadListCreate}
          >
            <Inject services={[NavigationPane, DetailsView, Toolbar]} />
          </FileManagerComponent>
          {!Browser.isDevice && (
            <ContextMenuComponent
              id="action-add-context-menu"
              ref={refMenu}
              items={actionAddContextMenuItems}
              select={onActionAddClick}
              beforeOpen={onBeforeOpen}
            />
          )}
          {!Browser.isDevice ? (
            <PopoverFilter trigger="legacy" placement="bottom" target="file_tb_filterscli">
              <PopoverBody>
                <OptionMenuFilter aria-hidden="true">
                  {filterList.map((filter) => (
                    <React.Fragment key={filter.id}>
                      <div
                        onClick={() => getCurrentFilters(filter.id)}
                        aria-hidden="true"
                        className="d-flex justify-content-between mb-2"
                      >
                        <span className="filter-name">{filter.name}</span>
                        {getCaretIcon(filter)}
                      </div>
                      <Collapse isOpen={filter.isOpen}>
                        {filter.id === 0 && (
                          <>
                            {states.map((state) => (
                              <div className="mb-3 d-flex" key={state.id}>
                                <Checkbox
                                  type="checkbox"
                                  id="filter-state"
                                  label=""
                                  checked={selectedState.some((x) => x === state.id)}
                                  onClick={(e) => onSelectedState(e, state)}
                                />
                                <span className="filter-i18-name">{getNameI18(state.i18List)}</span>
                              </div>
                            ))}
                          </>
                        )}
                        {filter.id === 1 && (
                          <>
                            {tags.map((tag) => (
                              <div className="mb-3 d-flex" key={tag.id}>
                                <Checkbox
                                  type="checkbox"
                                  id="filter-state"
                                  label=""
                                  checked={selectedTag.some((x) => x === tag.id)}
                                  onClick={(e) => onSelectedTag(e, tag)}
                                />
                                <span className="filter-i18-name">{getNameI18(tag.i18List)}</span>
                              </div>
                            ))}
                          </>
                        )}
                      </Collapse>
                    </React.Fragment>
                  ))}
                </OptionMenuFilter>
              </PopoverBody>
            </PopoverFilter>
          ) : null}
          <PublicFormModal isOpen={openPublicDocumentsModal} size="md">
            <ModalHeader
              className="modal-header"
              backgroundColor={colors.transparent}
              onClose={onClosePublicModal}
            />
            <ModalBody>
              <PublicDocuments
                onCancel={onClosePublicDocumentsModal}
                setShowConfirmPublic={setShowConfirm}
                setConfirmMessage={setConfirmMessage}
              />
            </ModalBody>
          </PublicFormModal>
          <UploadFormModal isOpen={openUploadDocumentsModal} size="md">
            <ModalHeader
              className="modal-header"
              backgroundColor={colors.transparent}
              onClose={onCloseUploadModal}
              canClose={false}
            />
            <ModalBody>
              <UploadDocuments
                onCancel={onCloseUploadModal}
                newUploadFiles={newUploadFiles}
                currentPath={refFileManager?.current?.path}
                setNewUploadFiles={setNewUploadFiles}
                refFileManager={refFileManager}
              />
            </ModalBody>
          </UploadFormModal>
        </>
      )}
      {loadingUpdate && <MainSpinner />}
      <ConfirmModal
        isOpen={showConfirmExistFile}
        toggle={onCancelExistFile}
        confirmMessages={confirmMessages}
        okText={t('general_button_ok')}
        onOk={onOkExistFile}
        cancelText={t('general_button_cancel')}
        onCancel={onCancelExistFile}
      />
      <ConfirmModal
        isOpen={showConfirm}
        toggle={onCancelPublic}
        confirmMessage={confirmMessage}
        okText={t('general_button_ok')}
        onOk={onOkPublic}
      />
      <AlertModal
        type="warning"
        isOpen={showWarning}
        toggle={onToggleWarning}
        message={t('document_alert_download')}
        okText={t('general_button_ok')}
        onOk={onToggleWarning}
      />
      <AlertModal
        type={alertType}
        isOpen={openAlert}
        toggle={onCloseAlert}
        message={alertMessage}
        okText={t('general_button_ok')}
        onOk={onCloseAlert}
      />
    </div>
  );
};

FileManager.defaultProps = {};

FileManager.propTypes = {
  clientId: PropTypes.string.isRequired,
  onToolbarCreate: PropTypes.func.isRequired,
  onToolbarClick: PropTypes.func.isRequired,
  onMenuClick: PropTypes.func.isRequired,
  onMenuOpen: PropTypes.func.isRequired,
  onActionAddClick: PropTypes.func.isRequired,
  onPopupOpen: PropTypes.func.isRequired,
  onPopUpClose: PropTypes.func.isRequired,
  refMenu: PropTypes.objectOf(PropTypes.shape()).isRequired,
  refFileManager: PropTypes.objectOf(PropTypes.shape()).isRequired,
  setLoadingCreateFolder: PropTypes.func.isRequired,
  loadingCreateFolder: PropTypes.bool.isRequired,
  setTotalSizeUpload: PropTypes.func.isRequired,
  totalSizeUpload: PropTypes.number.isRequired,
  isInputFile: PropTypes.func.isRequired,
  setInputFile: PropTypes.func.isRequired,
};

export default FileManager;
